import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { pageSEO } from '../utils/seo';

const ServiceGuidePage: React.FC = () => {
  const { i18n } = useTranslation();
  const seo = pageSEO.serviceGuide;
  const isEnglish = i18n.language.startsWith('en');

  const text = isEnglish ? {
    title: 'Service Guide',
    subtitle: 'Please check these details before renting a vehicle.',
    eligibility: 'Rental Eligibility',
    domesticTag: 'Korean',
    domesticTitle: 'Domestic Customers',
    domesticLicense: 'A valid driver’s license is required.',
    foreignTag: 'Foreigner',
    foreignTitle: 'International Customers',
    foreignPermit: 'International Driving Permit from a Geneva/Vienna Convention country is required.',
    foreignCoRental: 'Co-rental with a Korean driver’s license holder is required.',
    basicRequirements: 'Basic Requirements (All Vehicles)',
    age: 'Age 21 or older',
    license: 'Class 2 or higher driver’s license',
    experience: 'At least 1 year of driving experience',
    vanNote: '* Vehicles with 11+ seats require a Class 1 license.',
    process: 'Rental Process',
    pickupTag: 'Pickup',
    pickupTitle: 'Vehicle Pickup (3 Steps)',
    returnTag: 'Return',
    returnTitle: 'Vehicle Return (3 Steps)',
    pickupSteps: [
      ['Online Booking', 'Book easily through the real-time reservation system.'],
      ['Contract', 'Inspect the vehicle and complete the contract at the designated location.'],
      ['Start Rental', 'Pick up the vehicle and enjoy your trip.'],
    ],
    returnSteps: [
      ['Contact in Advance', 'Contact customer service 1 hour before return.'],
      ['Vehicle Inspection', 'Check the vehicle condition with staff.'],
      ['Complete Return', 'Finish return and settlement.'],
    ],
    cancellation: 'Cancellation and Refund Policy',
    cancelTiming: 'Cancellation Timing',
    refundRate: 'Refund Rate',
    refunds: [
      ['7 days before rental', '100% refund'],
      ['3-7 days before rental', '70% refund'],
      ['1-3 days before rental', '50% refund'],
      ['Rental day', 'No refund'],
    ],
    insurance: 'Insurance Guide',
    comprehensive: 'Comprehensive Insurance (Included)',
    bodily1: 'Bodily injury: Unlimited',
    property: 'Property damage: 20M KRW per accident',
    selfInjury: 'Self injury: 15M KRW per person',
    deductible: 'Deductible Guide',
    deductibleDesc: 'A deductible may apply depending on the claim amount and insurance type.',
    contactTitle: 'Have Questions?',
    contactDesc: 'Contact customer service. We will be happy to help.',
    hours: 'Weekdays 08:00 - 22:00 | Weekend and holiday consultation available',
  } : {
    title: '이용안내',
    subtitle: '렌터카 이용 전 꼭 확인해주세요',
    eligibility: '대여 자격',
    domesticTag: '내국인',
    domesticTitle: '국내 고객',
    domesticLicense: '유효한 운전면허증 필수',
    foreignTag: '외국인',
    foreignTitle: '해외 고객',
    foreignPermit: '제네바/비엔나 협약 가입국 국제운전면허증 소지자',
    foreignCoRental: '한국 운전면허 소지자와 공동 대여 필수',
    basicRequirements: '기본 자격 요건 (모든 차량)',
    age: '만 21세 이상',
    license: '2종 이상 운전면허 보유',
    experience: '운전경력 1년 이상',
    vanNote: '* 11인승 이상 차량은 1종 면허 필요',
    process: '대여 절차',
    pickupTag: '인수',
    pickupTitle: '차량 인수 (3단계)',
    returnTag: '반납',
    returnTitle: '차량 반납 (3단계)',
    pickupSteps: [
      ['온라인 예약', '실시간 예약 시스템으로 간편하게'],
      ['계약 체결', '지정 장소에서 차량 점검 및 계약서 작성'],
      ['대여 시작', '차량 인수 후 즐거운 여행'],
    ],
    returnSteps: [
      ['사전 연락', '반납 1시간 전 고객센터 연락'],
      ['차량 점검', '직원과 함께 차량 상태 확인'],
      ['대여 종료', '반납 완료 및 정산'],
    ],
    cancellation: '취소 및 환불 규정',
    cancelTiming: '취소 시점',
    refundRate: '환불율',
    refunds: [
      ['대여 7일 전', '100% 환불'],
      ['대여 3~7일 전', '70% 환불'],
      ['대여 1~3일 전', '50% 환불'],
      ['대여 당일', '환불 불가'],
    ],
    insurance: '보험 안내',
    comprehensive: '종합보험 가입 (기본 포함)',
    bodily1: '대인배상: 무제한',
    property: '대물배상: 사고당 2천만원',
    selfInjury: '자손: 1인당 1천5백만원',
    deductible: '자기부담금 안내',
    deductibleDesc: '사고 발생 시 청구 금액 및 보험 종류에 따라 자기부담금이 발생할 수 있습니다.',
    contactTitle: '궁금한 점이 있으신가요?',
    contactDesc: '고객센터로 문의해주세요. 친절하게 안내해드리겠습니다.',
    hours: '평일 08:00 - 22:00 | 주말 및 공휴일 상담 가능',
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodtimerent.kr/service-guide" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{text.title}</h1>
              <p className="text-lg text-gray-600">{text.subtitle}</p>
            </div>

            <section className="bg-white rounded-lg shadow-md p-8 mb-8">
              <SectionTitle title={text.eligibility} icon="check" />
              <div className="space-y-6">
                <GuideBlock tag={text.domesticTag} title={text.domesticTitle} items={[text.domesticLicense]} />
                <GuideBlock tag={text.foreignTag} title={text.foreignTitle} items={[text.foreignPermit, text.foreignCoRental]} />

                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">{text.basicRequirements}</h3>
                  <CheckList items={[text.age, text.license, text.experience]} />
                  <p className="mt-4 text-sm text-gray-500">{text.vanNote}</p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-lg shadow-md p-8 mb-8">
              <SectionTitle title={text.process} icon="bolt" />
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <StepHeader tag={text.pickupTag} title={text.pickupTitle} />
                  <div className="space-y-4">
                    {text.pickupSteps.map(([title, description], index) => (
                      <ProcessStep key={title} number={`${index + 1}`} title={title} description={description} />
                    ))}
                  </div>
                </div>
                <div>
                  <StepHeader tag={text.returnTag} title={text.returnTitle} />
                  <div className="space-y-4">
                    {text.returnSteps.map(([title, description], index) => (
                      <ProcessStep key={title} number={`${index + 1}`} title={title} description={description} />
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-lg shadow-md p-8 mb-8">
              <SectionTitle title={text.cancellation} icon="clock" />
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{text.cancelTiming}</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{text.refundRate}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {text.refunds.map(([timing, rate]) => (
                      <tr key={timing}>
                        <td className="px-6 py-4 text-gray-700">{timing}</td>
                        <td className="px-6 py-4"><span className="text-gray-900 font-semibold">{rate}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white rounded-lg shadow-md p-8 mb-8">
              <SectionTitle title={text.insurance} icon="shield" />
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">{text.comprehensive}</h3>
                  <CheckList items={[text.bodily1, text.property, text.selfInjury]} />
                </div>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">{text.deductible}</h3>
                  <p className="text-sm text-gray-600">{text.deductibleDesc}</p>
                </div>
              </div>
            </section>

            <section className="bg-gray-800 rounded-lg shadow-md p-8 text-white">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4">{text.contactTitle}</h2>
                <p className="mb-6">{text.contactDesc}</p>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:1600-4412" className="text-3xl font-bold hover:text-white/90">1600-4412</a>
                </div>
                <p className="text-sm text-white/90">{text.hours}</p>
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

interface SectionTitleProps {
  title: string;
  icon: 'check' | 'bolt' | 'clock' | 'shield';
}

const SectionTitle: React.FC<SectionTitleProps> = ({ title, icon }) => {
  const paths = {
    check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  };

  return (
    <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b flex items-center">
      <svg className="w-7 h-7 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[icon]} />
      </svg>
      {title}
    </h2>
  );
};

interface GuideBlockProps {
  tag: string;
  title: string;
  items: string[];
}

const GuideBlock: React.FC<GuideBlockProps> = ({ tag, title, items }) => (
  <div>
    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm mr-3 border border-gray-300">{tag}</span>
      {title}
    </h3>
    <ul className="ml-12 space-y-2 text-gray-600">
      {items.map((item) => (
        <li key={item} className="flex items-start">
          <span className="text-gray-600 mr-2">•</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const CheckList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-2 text-gray-600">
    {items.map((item) => (
      <li key={item} className="flex items-start">
        <svg className="w-5 h-5 text-gray-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {item}
      </li>
    ))}
  </ul>
);

interface StepHeaderProps {
  tag: string;
  title: string;
}

const StepHeader: React.FC<StepHeaderProps> = ({ tag, title }) => (
  <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center">
    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm mr-3 border border-gray-300">{tag}</span>
    {title}
  </h3>
);

interface ProcessStepProps {
  number: string;
  title: string;
  description: string;
}

const ProcessStep: React.FC<ProcessStepProps> = ({ number, title, description }) => (
  <div className="flex items-start">
    <div className="flex-shrink-0 w-10 h-10 bg-gray-700 text-white rounded-full flex items-center justify-center font-bold mr-4">
      {number}
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
    </div>
  </div>
);

export default ServiceGuidePage;
